import { HierarchyArea } from '@plarium/hierarchy-react';
import React, { useState, useMemo, useCallback } from 'react';

import { percolateRequest } from 'api/utils/percolateRequest';

import {
  Section,
  Grid,
  Button,
  Typography,
  Help,
  Stack,
  Tooltip,
} from '@gogame/components';
import {
  withForm,
  FormField,
  getIn,
  SidePageV2,
  FormFieldArray,
} from '@gogame/form';
import { useChildComponent } from '@gogame/hooks-ts';
import { dateFNS, fromUTCToTimeZone } from '@gogame/intl-ts';
import Permissions from '@gogame/permissions';
import { useScreenModalV2 } from '@gogame/screens';

import {
  loadBusinessPartners,
  loadAffiliates,
  loadExpenseTypes,
  loadApplications,
  loadImportance,
} from 'scenes/FinancialResults/sources/fields';
import { loadSpend as _loadSpend } from 'scenes/FinancialResults/sources/financialResults';
import { loadInvoicesSpend as _loadInvoicesSpend } from 'scenes/FinancialResults/sources/invoices';
import {
  AFFILIATE,
  APPLICATION,
  ADJUSTMENT_REASON,
  TYPE,
} from 'scenes/SpendAdjustments/components/SpendAdjustmentForm/constants';

import { useUploadAttachment } from 'scenes/FinancialResults/data/mutations';

import DiscrepanciesWarningNumber from './GAPWarningDialog/DiscrepanciesWarningNumber';
import HigherDialog from './GAPWarningDialog/HigherDialog';
import LowerDialog from './GAPWarningDialog/LowerDialog';
import InvoicesFieldArray from './InvoicesFieldArray';
import Spend from './Spend';
import SpendAdjustmentCard from './SpendAdjustmentCard';
import SpendAdjustmentForm from './SpendAdjustmentForm';
import {
  BUSINESS_PARTNER,
  AFFILIATES,
  PERIOD,
  EXPENSE_TYPE,
  APPLICATIONS,
  IMPORTANCE_TYPE,
  DESCRIPTION,
  INVOICES,
  SPEND,
  ATTACHMENTS,
  DEPENDENCIES,
  CREATION_GRADE_SAVE,
  CREATION_GRADE_HIGHER_DIALOG,
  CREATION_GRADE_LOWER_DIALOG,
  SPEND_ADJUSTMENTS,
  SPEND_ADJUSTMENT_REASON_ID,
} from './constants';
import {
  expenseTypeParamsSchema,
  periodParamsSchema,
  applicationParamsSchema,
  spendParamsSchema,
  invoicesParamsSchema,
  financialResultSchema,
} from './schemas';

const PREPAY_EXPANSE_TYPE = 1;
const loadSpend = percolateRequest(_loadSpend);
const loadInvoicesSpend = percolateRequest(_loadInvoicesSpend);
const prevMonth = dateFNS.subMonths(new Date(), 1);
const FinancialResultForm = withForm({
  enableReinitialize: true,
  actions: [
    BUSINESS_PARTNER,
    AFFILIATES,
    EXPENSE_TYPE,
    PERIOD,
    APPLICATIONS,
    IMPORTANCE_TYPE,
    DESCRIPTION,
    INVOICES,
  ],
  validationSchema: financialResultSchema,
  initialValues: { importanceType: 0, attachments: [] },
})(({
  submitForm,
  validateForm,
  values,
  dirty,
  onClose,
  actions,
  loading,
  setFieldValue,
  hierarchyState,
  onExited,
  open,
  isEdit,
  loadingCount,
  ...props
}) => {
  const [openEnd, setOpenEnd] = useState(false);
  const [currencyCode, setCurrencyCode] = useState(null);
  const [childComponent, openChildComponent] = useChildComponent();
  const [spendAdjustmentsData, setSpendAdjustmentData] = useState({
    [AFFILIATE]: [],
    [APPLICATION]: [],
    [BUSINESS_PARTNER]: [],
  });
  const showLowerWarningDialog = useScreenModalV2(LowerDialog);
  const showHigherWarningDialog = useScreenModalV2(HigherDialog);
  const showDiscrepanciesWarning = useScreenModalV2(DiscrepanciesWarningNumber);

  const businessPartnerData = hierarchyState?.businessPartnerId?.data || [];
  const handleLoadInvoicesSpend = useMemo(() => {
    return (params) => {
      const { id = null } = values;
      return loadInvoicesSpend({
        ...params,
        id: id,
      });
    };
  }, [values.id]);

  const affiliatesInvoiceLevel = useMemo(() => {
    if (businessPartnerData.length && values[BUSINESS_PARTNER]) {
      const businessPartner = businessPartnerData.find(
        (bp) => bp.id === values[BUSINESS_PARTNER]
      );
      return businessPartner?.invoiceLevel === 1;
    }
    return false;
  }, [values[BUSINESS_PARTNER], businessPartnerData]);

  const handleNormalizeApplications = (_value, options) => {
    return options
      .filter(({ isSpentPositive }) => isSpentPositive)
      .map(({ id }) => id);
  };

  const handleExpenseTypesLoad = () => {
    const value = values[EXPENSE_TYPE];
    const data =
      (hierarchyState &&
        hierarchyState[EXPENSE_TYPE] &&
        hierarchyState[EXPENSE_TYPE].data) ||
      [];
    const expenseType = data.find(({ id }) => id === value) || {};
    setOpenEnd(expenseType.openPeriod);
  };

  const handleChangeExpenseType = useCallback(
    (_e, value) => {
      if (
        values[EXPENSE_TYPE] === PREPAY_EXPANSE_TYPE &&
        value !== PREPAY_EXPANSE_TYPE
      ) {
        setFieldValue(PERIOD, null);
      }
      const data =
        (hierarchyState &&
          hierarchyState[EXPENSE_TYPE] &&
          hierarchyState[EXPENSE_TYPE].data) ||
        [];
      const expenseType = data.find(({ id }) => id === value) || {};

      const firstDate = Array.isArray(values[PERIOD])
        ? values[PERIOD][0]
        : undefined;

      if (firstDate) {
        if (openEnd && !expenseType.openPeriod) {
          setFieldValue(PERIOD, [firstDate, firstDate]);
        } else if (!openEnd && expenseType.openPeriod) {
          setFieldValue(PERIOD, [firstDate, null]);
        }
      }

      setOpenEnd(expenseType.openPeriod);
    },
    [values[EXPENSE_TYPE], hierarchyState?.[EXPENSE_TYPE]?.data]
  );

  const handleSpendLoad = ({ data: spend }) => {
    setCurrencyCode(spend.originalSpend.currencyCode);

    if (dirty) {
      setFieldValue(IMPORTANCE_TYPE, spend.importanceType);
    }
  };

  const loadMonthRangeDataSource = React.useCallback(
    ({ [EXPENSE_TYPE]: expenseTypeId }) => {
      const data =
        (hierarchyState &&
          hierarchyState[EXPENSE_TYPE] &&
          hierarchyState[EXPENSE_TYPE].data) ||
        [];
      const expenseType = data.find(({ id }) => id === expenseTypeId) || {};
      return {
        openEnd: expenseType.openPeriod,
      };
    },
    [hierarchyState[EXPENSE_TYPE]]
  );

  const uploadAttachment = useUploadAttachment();

  const handleUploadAttachment = (files) => {
    return uploadAttachment(files).then(({ id, name }) => ({
      name,
      attachmentId: id,
    }));
  };

  const handleSubmit = async () => {
    validateForm(values);

    const {
      financialResultCreationGrade,
      spendingSource,
      spendingGapAbsolute,
      spendingGapFactor,
      ioDiscrepancyDetails,
    } = getIn(hierarchyState, `${INVOICES}.data`) || {};
    if (!Number.isFinite(financialResultCreationGrade)) {
      return;
    }

    switch (financialResultCreationGrade) {
      case CREATION_GRADE_HIGHER_DIALOG:
        await showHigherWarningDialog({
          spendingGapFactor,
          spendingGapAbsolute,
          spendingSource,
        });
        break;
      case CREATION_GRADE_LOWER_DIALOG:
        await showLowerWarningDialog({
          spendingGapFactor,
          spendingGapAbsolute,
          spendingSource,
        });
        if (!ioDiscrepancyDetails) {
          submitForm();
        }
        break;
      case CREATION_GRADE_SAVE:
      default:
        if (!ioDiscrepancyDetails) {
          submitForm();
        }
        break;
    }
    if (ioDiscrepancyDetails) {
      await showDiscrepanciesWarning(ioDiscrepancyDetails);
      submitForm();
    }
  };
  const handleOpenAddSpendAdjustment = useCallback(() => {
    openChildComponent(SpendAdjustmentForm, {
      title: 'Add Spend Adjustment',
      initialValues: {
        [AFFILIATE]: values[AFFILIATES]?.[0],
        [APPLICATION]: values[APPLICATIONS][0],
        [ADJUSTMENT_REASON]: SPEND_ADJUSTMENT_REASON_ID,
        [TYPE]: 1,
      },
      // maxDate:
      isCampaignField: false,
      isDisabledAdjustReason: true,
      maxDate: dateFNS.subDays(fromUTCToTimeZone(values?.[PERIOD]?.[0]), 1),
      periodHelper: (
        <Stack>
          Available period for selection is up to the
          <Stack spacing={1} direction={'row'} alignItems={'center'}>
            <Stack>month of this FR</Stack>
            <Help placement={'right-start'}>
              For later spend fixes, please use a Spend Adjustment and then
              create the relevant Financial Result
            </Help>
          </Stack>
        </Stack>
      ),
      getAffiliate: () =>
        spendAdjustmentsData[BUSINESS_PARTNER].find(
          ({ id }) => id === values[BUSINESS_PARTNER]
        ).invoiceLevel
          ? spendAdjustmentsData[AFFILIATE].filter(({ id }) =>
              values[AFFILIATES].includes(id)
            )
          : spendAdjustmentsData[AFFILIATE],
      getApplications: () =>
        values[APPLICATIONS].length
          ? spendAdjustmentsData[APPLICATION].filter(({ id }) =>
              values[APPLICATIONS].includes(id)
            )
          : spendAdjustmentsData[APPLICATION],
      getSpendType: () => [{ id: 3, name: 'Cost Adjustment' }],
      getReasons: () => [{ id: 8, name: 'Refund - FR' }],
      onSubmit: (response) => {
        const data = values.spendAdjustments?.length
          ? [...values.spendAdjustments, response]
          : [response];
        setFieldValue(SPEND_ADJUSTMENTS, data);
      },
    });
  }, [
    spendAdjustmentsData[AFFILIATE],
    spendAdjustmentsData[APPLICATION],
    spendAdjustmentsData[BUSINESS_PARTNER],
    values[APPLICATIONS],
    values[AFFILIATES],
    values[SPEND_ADJUSTMENTS],
    values[PERIOD],
  ]);

  const handleEditSpendAdjustment = useCallback(
    (index) => {
      openChildComponent(SpendAdjustmentForm, {
        title: 'Edit Spend Adjustment',
        initialValues: values[SPEND_ADJUSTMENTS][index],
        isCampaignField: false,
        isDisabledAdjustReason: true,
        getAffiliate: () =>
          spendAdjustmentsData[BUSINESS_PARTNER].find(
            ({ id }) => id === values[BUSINESS_PARTNER]
          ).invoiceLevel
            ? spendAdjustmentsData[AFFILIATE].filter(({ id }) =>
                values[AFFILIATES].includes(id)
              )
            : spendAdjustmentsData[AFFILIATE],
        getApplications: () =>
          values[APPLICATIONS].length
            ? spendAdjustmentsData[APPLICATION].filter(({ id }) =>
                values[APPLICATIONS].includes(id)
              )
            : spendAdjustmentsData[APPLICATION],
        getSpendType: () => [{ id: 1, name: 'Cost Adjustment' }],
        getReasons: () => [{ id: 8, name: 'Refund - FR' }],
        onSubmit: (response) => {
          const data = values.spendAdjustments.map((spendAdjustment, i) =>
            i === index ? response : spendAdjustment
          );
          setFieldValue(SPEND_ADJUSTMENTS, data);
        },
      });
    },
    [
      spendAdjustmentsData[AFFILIATE],
      spendAdjustmentsData[APPLICATION],
      spendAdjustmentsData[BUSINESS_PARTNER],
      values[SPEND_ADJUSTMENTS],
      values[APPLICATIONS],
      values[AFFILIATES],
    ]
  );

  const handleValidateApplication = (id) =>
    !values[APPLICATIONS].includes(id) &&
    'Should be the same as FR’s application';
  const periodMaxData = useMemo(() => {
    if (values[EXPENSE_TYPE] && values[EXPENSE_TYPE] === PREPAY_EXPANSE_TYPE) {
      return;
    } else return prevMonth;
  }, [values[EXPENSE_TYPE]]);

  const handleBpDisabled = (opt) => {
    return !!opt.warnings?.length;
  };

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <React.Fragment>
        <SidePageV2.Title
          title={props.title}
          subTitle={props.name}
          entityId={props.entityId}
        />
        <Permissions actions={actions}>
          {({ isActionAllowed }) => (
            <SidePageV2.NewBody>
              <Section title={'General'}>
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    <FormField.Select
                      padding={'0'}
                      label={'Business partner'}
                      required={true}
                      name={BUSINESS_PARTNER}
                      source={loadBusinessPartners}
                      placeholder={'Select partner'}
                      onDataLoad={({ data }) => {
                        setSpendAdjustmentData((prev) => ({
                          ...prev,
                          [BUSINESS_PARTNER]: data,
                        }));
                      }}
                      lazy={false}
                      isOptionDisabled={handleBpDisabled}
                      disabled={!isActionAllowed(BUSINESS_PARTNER)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormField.MultiSelect
                      padding={'0'}
                      label={'Affiliate'}
                      name={AFFILIATES}
                      source={loadAffiliates}
                      depends={DEPENDENCIES[AFFILIATES]}
                      placeholder={'Select affiliate'}
                      onDataLoad={({ data }) => {
                        setSpendAdjustmentData((prev) => ({
                          ...prev,
                          [AFFILIATE]: data,
                        }));
                      }}
                      active={Boolean(values[BUSINESS_PARTNER])}
                      disabled={
                        !isActionAllowed(AFFILIATES) || !affiliatesInvoiceLevel
                      }
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormField.Select
                      padding={'0'}
                      label={'Expense type'}
                      name={EXPENSE_TYPE}
                      depends={DEPENDENCIES[EXPENSE_TYPE]}
                      active={expenseTypeParamsSchema.isValidSync(values, {
                        context: { bpInvoiceLevel: affiliatesInvoiceLevel },
                      })}
                      source={loadExpenseTypes}
                      disabled={!isActionAllowed(EXPENSE_TYPE)}
                      onDataLoad={handleExpenseTypesLoad}
                      onChange={handleChangeExpenseType}
                      placeholder={'Select type'}
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormField.MonthRangePicker
                      padding={'0'}
                      maxDate={periodMaxData}
                      maxRange={11}
                      label={'Period'}
                      openPeriod={openEnd}
                      placeholder={''}
                      name={PERIOD}
                      depends={DEPENDENCIES[PERIOD]}
                      source={loadMonthRangeDataSource}
                      active={periodParamsSchema.isValidSync(values, {
                        context: { bpInvoiceLevel: affiliatesInvoiceLevel },
                      })}
                      disabled={
                        !!loadingCount || loading || !isActionAllowed(PERIOD)
                      }
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormField.Select
                      padding={'0'}
                      label={'Importance'}
                      name={IMPORTANCE_TYPE}
                      source={loadImportance}
                      lazy={false}
                      disabled={!isActionAllowed(IMPORTANCE_TYPE)}
                      placeholder={'Select importance'}
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormField.MultiSelect
                      padding={'0'}
                      label={'Applications'}
                      name={APPLICATIONS}
                      depends={DEPENDENCIES[APPLICATIONS]}
                      source={loadApplications}
                      onDataLoad={({ data }) => {
                        setSpendAdjustmentData((prev) => ({
                          ...prev,
                          [APPLICATION]: data,
                        }));
                      }}
                      lazy={false}
                      active={applicationParamsSchema.isValidSync(values, {
                        context: { bpInvoiceLevel: affiliatesInvoiceLevel },
                      })}
                      disabled={!isActionAllowed(APPLICATIONS)}
                      placeholder={'All'}
                      withAll={true}
                      normalizeValue={
                        dirty ? handleNormalizeApplications : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField.TextField
                      multiline={true}
                      padding={'0'}
                      minRows={3}
                      maxRows={15}
                      label={'Description'}
                      name={DESCRIPTION}
                      disabled={!isActionAllowed(DESCRIPTION)}
                    />
                  </Grid>
                </Grid>
              </Section>
              <HierarchyArea
                name={SPEND}
                source={loadSpend}
                depends={DEPENDENCIES[SPEND]}
                active={spendParamsSchema.isValidSync(values, {
                  context: { bpInvoiceLevel: affiliatesInvoiceLevel },
                })}
                onDataLoad={handleSpendLoad}
              >
                {({ area }) =>
                  area.data || area.loading || area.error ? (
                    <Section>
                      <Spend
                        data={area.data}
                        loading={area.loading}
                        error={area.error}
                      />
                    </Section>
                  ) : null
                }
              </HierarchyArea>
              <Section
                title={
                  <Stack spacing={1} direction={'row'} alignItems={'center'}>
                    <Typography variant={'subtitle1'} sx={{ fontWeight: 500 }}>
                      Refunds
                    </Typography>
                    <Help>
                      Refund to be used only for closed financial months
                    </Help>
                  </Stack>
                }
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Tooltip
                      title={
                        isEdit
                          ? 'Refund cannot be added for existing  financial results'
                          : undefined
                      }
                    >
                      <span>
                        <Button
                          add={true}
                          onClick={handleOpenAddSpendAdjustment}
                          disabled={!values[APPLICATIONS] || isEdit}
                          variant={'outlined'}
                        >
                          Refund
                        </Button>
                      </span>
                    </Tooltip>
                  </Grid>
                  {values?.spendAdjustments && (
                    <FormFieldArray name={SPEND_ADJUSTMENTS}>
                      {({ remove }) =>
                        values?.spendAdjustments?.map((adjustment, index) => (
                          <Grid item xs={6} key={index}>
                            <SpendAdjustmentCard
                              onRemove={remove}
                              index={index}
                              onValidateApplication={handleValidateApplication}
                              handleEditSpendAdjustment={
                                handleEditSpendAdjustment
                              }
                              isEdit={isEdit}
                              model={adjustment}
                            />
                          </Grid>
                        ))
                      }
                    </FormFieldArray>
                  )}
                </Grid>
              </Section>
              <InvoicesFieldArray
                name={INVOICES}
                depends={DEPENDENCIES[INVOICES]}
                source={handleLoadInvoicesSpend}
                active={invoicesParamsSchema.isValidSync(values, {
                  context: { bpInvoiceLevel: affiliatesInvoiceLevel },
                })}
                financialResultCurrency={currencyCode}
                disabled={!isActionAllowed(INVOICES) || !currencyCode}
              />
              <Section title={'Attachments'}>
                <FormField.AttachmentField
                  dataCy={'attachFiles'}
                  name={ATTACHMENTS}
                  label={'Select files'}
                  tooltip={'.pdf, .png, .jpg, .jpeg, .doc, .docx, .xls, .xlsx'}
                  accept={
                    '.pdf, image/png,  image/jpg, image/jpeg, .doc, .docx, .xls, .xlsx'
                  }
                  onUpload={handleUploadAttachment}
                  variant={'outlined'}
                  direction={'column'}
                  multiple={true}
                  padding={0}
                />
              </Section>
            </SidePageV2.NewBody>
          )}
        </Permissions>
        <SidePageV2.Actions>
          <SidePageV2.CloseButton />
          <SidePageV2.SubmitButton onSubmit={handleSubmit} />
        </SidePageV2.Actions>
      </React.Fragment>
      {childComponent}
    </SidePageV2>
  );
});

export default FinancialResultForm;
